<template>
  <div class="container">
    <!--
      日 / 週 / 月 選択エリア
      期間選択エリアの表示切替のトリガーです。
    -->
    <div>
      <md-radio
        v-for="term in termTypes"
        :key="term.id"
        v-model="_termType"
        :value="term.id"
        >{{ term.label }}</md-radio
      >
    </div>
    <!-- 期間選択エリア -->
    <div>
      <transition mode="out-in">
        <!-- 週の選択 -->
        <div v-if="isWeekMode" key="w">
          <div class="term-input">
            <md-field class="week-count">
              <md-input
                v-model.number="_weekCount"
                type="number"
                :min="1"
                required
              ></md-input>
            </md-field>
            <div class="divider">週間を単位として</div>
          </div>
          <div class="term-input">
            <gl-input-date
              :min="minDate"
              :max="maxDate"
              :value.sync="_dateFrom"
            />
            <div class="divider">から</div>
            <md-field class="week-count">
              <md-input
                v-model.number="_weekTerm"
                type="number"
                :min="1"
                required
              ></md-input>
            </md-field>
            <div class="divider">期間の推移を見る</div>
          </div>
        </div>
        <!-- 月の選択 -->
        <div v-if="isMonthMode" key="m">
          <div class="term-input">
            <md-field class="week-count">
              <md-input
                v-model.number="_monthCount"
                type="number"
                :min="1"
                required
              ></md-input>
            </md-field>
            <div class="divider">ヶ月を単位として</div>
          </div>
          <div class="term-input">
            <gl-select-month
              :min="minDate"
              :max="maxDate"
              :value.sync="monthFrom"
            />
            <div class="divider">から</div>
            <md-field class="week-count">
              <md-input
                v-model.number="_monthTerm"
                type="number"
                :min="1"
                required
              ></md-input>
            </md-field>
            <div class="divider">期間の推移を見る</div>
          </div>
        </div>
      </transition>
      <div>
        <gl-sub-text>
          {{ message }}
        </gl-sub-text>
      </div>
    </div>
    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      {{ snackbar.message }}
    </md-snackbar>
  </div>
</template>

<script>
import moment from 'moment'
import GlSelectMonth from '@/components/common/GlSelectMonth.vue'
import GlInputDate from '@/components/common/GlInputDate.vue'
import ErrorReport from '@/mixins/ErrorReport'
import QueryFilterRestoreMixin from '@/mixins/QueryFilterRestoreMixin'
import {
  getEndDateByWeekCount,
  dateLimit,
  toHumanFriendlyFormat,
} from '@/util/date'

// 通常（このコンポーネントへの入力や出力）で利用する日付フォーマット
const DATE_FORMAT = 'YYYYMMDD'
// input:dateタグでValueに使われる日付フォーマット
const DATE_FORMAT_ON_INPUT_TAG = 'YYYY-MM-DD'
// 週の初期値設定
const DEFAULT_WEEK_COUNT = 13
// 週の初期値設定
const DEFAULT_MONTH_COUNT = 4

/**
 * 期間選択コンポーネント
 *
 * 内部的には開始、終了日だけを保持し、週や月の値は現在選択されている日付
 * から算出して取得・設定します。
 *
 * なので、選択方式に週や月を選んでいてもこのコンポーネントから取得できるのは "日" です。
 */
export default {
  mixins: [ErrorReport, QueryFilterRestoreMixin],
  data() {
    return {
      termTypes: [{ id: 'w', label: '週' }, { id: 'm', label: '月' }],
      // Snackbarの表示制御用のデータ
      snackbar: {
        show: false,
        duration: 10000,
        message: '',
      },
    }
  },
  components: { GlSelectMonth, GlInputDate },
  props: {
    // 選択している選択種別（週 / 月）
    termType: {
      type: String,
      default: 'w',
    },
    dateFrom: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    dateTo: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    weekCount: {
      type: Number,
    },
    weekTerm: {
      type: Number,
    },
    monthCount: {
      type: Number,
    },
    monthTerm: {
      type: Number,
    },
  },
  watch: {
    /**
     * 最大日付の変更時、現在の日付値が上限を超えていないか確認し、
     * 超えている場合は新しい上限に自動調整します。
     */
    maxDate() {
      this.adjustRangeIfNeeded()
    },
    minDate() {
      this.adjustRangeIfNeeded()
    },
    /**
     * 選択種別の変更時、現在設定されている開始・終了日から、変更先の種別で適切となる
     * 開始・終了日に変換します。
     *
     * 例）"日" ⇒ "月" に変更した場合、"開始日" は月初の日付になり、"終了日" は月末の日付になります。
     *     2019/01/14～2019/05/03 -> 2019/01(2019/01/01)～2019/05(2019/05/31)
     */
    termType(value) {
      this.$emit('update:termType', value)
      let dateFrom = this.dateFrom
      let dateTo = this.dateTo

      // dateFrom, dateToはpropでもらい、親コンポーネントと.syncするので、
      // `update:dateFrom`, `update:dateTo` をemitするだけです。
      switch (value) {
        case 'w':
          // 週の場合終了日は表示されませんが、内部的には開始日からx週間後の日付を保持します。
          this.$emit(
            'update:dateTo',
            getEndDateByWeekCount(dateFrom, this.weekCount * this.weekTerm),
          )
          break
        case 'm':
          dateFrom = moment(dateFrom, DATE_FORMAT_ON_INPUT_TAG)
            .startOf('month')
            .format(DATE_FORMAT)
          dateTo = moment(dateTo, DATE_FORMAT_ON_INPUT_TAG)
            .endOf('month')
            .format(DATE_FORMAT)

          dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

          this.$emit('update:dateTo', dateTo)
          this.$emit('update:dateFrom', dateFrom)
          if (this.monthTerm > this.maxMonthTerm) {
            this.$emit('update:monthTerm', this.maxMonthTerm)
          }
          break
        default:
          break
      }
    },
  },
  methods: {
    adjustRangeIfNeeded() {
      let dateFrom = this.dateFrom
      let dateTo = this.dateTo
      // QueryFilterRestoreMixinのcomputedを参照
      if (this._queryFilterRestoring) return

      const adjustedFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
      const adjustedTo = dateLimit(dateTo, this.minDate, this.maxDate)

      if (adjustedFrom !== dateFrom || adjustedTo !== dateTo) {
        this.$emit('update:dateFrom', adjustedFrom)
        this.$emit('update:dateTo', adjustedTo)
        this.snackbar.message =
          '指定期間が選択可能な期間外だったため、自動調整しました。'
        this.snackbar.show = true
      }
    },
    // QueryFilterRestoreMixinのmethodsを上書き
    // 分析条件の復元が完了したときの処理
    onQueryFilterRestoreCompleted() {
      this.adjustRangeIfNeeded()
    },
  },
  computed: {
    minDate() {
      return this.$store.getters.calendarEffectiveMinDate
    },
    maxDate() {
      return this.$store.getters.calendarEffectiveMaxDate
    },
    _termType: {
      get() {
        if (!this.termTypes.some(m => m.id === this.termType)) {
          this.$emit('update:termType', this.termTypes[0].id || 'w')
        }
        return this.termType
      },
      set(newVal) {
        this.$emit('update:termType', newVal)
      },
    },
    isWeekMode() {
      return this.termType === 'w'
    },
    isMonthMode() {
      return this.termType === 'm'
    },
    maxWeekTerm() {
      const days = moment(this.maxDate).diff(this.dateFrom, 'days')
      const diff = Math.ceil(days / (this.weekCount * 7))
      return diff
    },
    maxMonthTerm() {
      const months = moment(this.maxDate).diff(this.dateFrom, 'months')
      const diff = months + 1
      return Math.ceil(diff / this.monthCount)
    },
    _weekTerm: {
      get() {
        return this.weekTerm
      },
      set(newVal) {
        if (newVal === '') {
          // キーボード操作で値をDeleteしてしまった場合など空文字が
          // 来ることがある。その場合、元の入力値のままとする。
          // 表示上は値が消えていることに注意！
          return
        }

        const weekDays = this.weekCount * newVal * 7

        let dateTo = moment(this.dateFrom)
          .add(weekDays, 'days')
          .subtract(1, 'days')
          .format(DATE_FORMAT)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

        this.$emit('update:dateTo', dateTo)
        this.$emit('update:weekTerm', newVal)
      },
    },
    /**
     * 週単位数
     */
    _weekCount: {
      get() {
        return this.weekCount
      },
      set(newVal) {
        if (newVal === '') {
          // キーボード操作で値をDeleteしてしまった場合など空文字が
          // 来ることがある。その場合、元の入力値のままとする。
          // 表示上は値が消えていることに注意！
          return
        }

        const weekDays = newVal * this.weekTerm * 7

        let dateTo = moment(this.dateFrom)
          .add(weekDays, 'days')
          .subtract(1, 'days')
          .format(DATE_FORMAT)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

        this.$emit('update:dateTo', dateTo)
        this.$emit('update:weekCount', newVal)
      },
    },
    _dateFrom: {
      get() {
        return this.dateFrom
      },
      set(newVal) {
        const weekDays = this.weekCount * this.weekTerm * 7

        let dateTo = moment(newVal)
          .add(weekDays, 'days')
          .subtract(1, 'days')
          .format(DATE_FORMAT)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

        this.$emit('update:dateFrom', newVal)
        this.$emit('update:dateTo', dateTo)
      },
    },
    monthFrom: {
      get() {
        return moment(this.dateFrom).format('YYYYMM')
      },
      set(newVal) {
        newVal = moment(newVal, DATE_FORMAT)
          .startOf('month')
          .format(DATE_FORMAT)
        const dateFrom = dateLimit(newVal, this.minDate, this.maxDate)
        const months = this.monthCount * this.monthTerm

        let dateTo = moment(dateFrom)
          .add(months, 'months')
          .subtract(1, 'days')
          .format(DATE_FORMAT)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

        this.$emit('update:dateFrom', dateFrom)
        this.$emit('update:dateTo', dateTo)
      },
    },
    _monthTerm: {
      get() {
        return this.monthTerm
      },
      set(newVal) {
        if (newVal === '') {
          // キーボード操作で値をDeleteしてしまった場合など空文字が
          // 来ることがある。その場合、元の入力値のままとする。
          // 表示上は値が消えていることに注意！
          return
        }

        const months = this.monthCount * newVal

        let dateTo = moment(this.dateFrom)
          .add(months, 'months')
          .subtract(1, 'days')
          .format(DATE_FORMAT)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

        this.$emit('update:dateTo', dateTo)
        this.$emit('update:monthTerm', newVal)
      },
    },
    _monthCount: {
      get() {
        return this.monthCount
      },
      set(newVal) {
        if (newVal === '') {
          // キーボード操作で値をDeleteしてしまった場合など空文字が
          // 来ることがある。その場合、元の入力値のままとする。
          // 表示上は値が消えていることに注意！
          return
        }

        const months = newVal * this.monthTerm

        let dateTo = moment(this.dateFrom)
          .add(months, 'months')
          .subtract(1, 'days')
          .format(DATE_FORMAT)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

        this.$emit('update:dateTo', dateTo)
        this.$emit('update:monthCount', newVal)
      },
    },
    message() {
      let term
      let maxTerm
      switch (this.termType) {
        case 'w':
          term = this.weekTerm
          maxTerm = this.maxWeekTerm
          break
        case 'm':
          term = this.monthTerm
          maxTerm = this.maxMonthTerm
          break
      }

      if (term > maxTerm) {
        return `データ保持期間を超えているため、${maxTerm}期間（～${toHumanFriendlyFormat(
          this.dateTo,
        )}）までしか分析されません。`
      }

      return undefined
    },
  },
  async created() {
    let dateFrom = this.dateFrom
    let dateTo = this.dateTo
    let weekCount = this.weekCount
    let weekTerm = this.weekTerm
    let monthCount = this.monthCount
    let monthTerm = this.monthTerm

    if (!dateFrom || !dateTo) {
      dateFrom = moment(this.maxDate)
        .subtract(DEFAULT_WEEK_COUNT, 'week')
        .add(1, 'days')
        .format(DATE_FORMAT)
      dateTo = this.maxDate
      // 検索期間が検索期限を超えていた場合の処理
      dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
      dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
      this.$emit('update:dateFrom', dateFrom)
      this.$emit('update:dateTo', dateTo)
    }
    if (!this.weekCount) {
      weekCount = 1
    }
    if (!this.weekTerm) {
      weekTerm = DEFAULT_WEEK_COUNT
    }
    if (!this.monthCount) {
      monthCount = 1
    }
    if (!this.monthTerm) {
      monthTerm = DEFAULT_MONTH_COUNT
    }

    this.$emit('update:weekCount', weekCount)
    this.$emit('update:weekTerm', weekTerm)
    this.$emit('update:monthCount', monthCount)
    this.$emit('update:monthTerm', monthTerm)
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.term-input {
  display: flex;
}

.week-count {
  width: 70px;
  // なぜかmd-inputの中のinputにもwidthを適用しないと
  // 横幅が合わなかった（上のwidth指定だけではinputの
  // widthが変わらなかった）
  input {
    width: 70px;
  }
}

.divider {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 16px;
  margin-right: 16px;
  white-space: nowrap;
}

// 期間選択エリアの切替アニメーション
.v-enter-active,
.v-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}
.v-enter {
  transform: translateX(-50%);
}
.v-leave-to {
  transform: translateX(50%);
}
</style>
